import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Button, Modal, Form, Input, message, Empty, Typography, Popconfirm, Space, Upload, Dropdown, Segmented, Select, Table, Result } from 'antd';
import AppLoading from '../components/AppLoading';
import { PlusOutlined, RadarChartOutlined, DashboardOutlined, DeleteOutlined, DeleteRowOutlined, DownloadOutlined, UploadOutlined, AppstoreOutlined, BarsOutlined, SearchOutlined, ReloadOutlined, ThunderboltOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useDeployStore } from '../stores/deployStore';
import { fetchScenes, createScene, deleteScene } from '../api/scenes';
import { exportScenesBackup, importScenesBackup, backupErrorHandler, backupExportErrorHandler, type BackupMode } from '../utils/backup';
import { fetchBackupPolicy, type BackupPolicyView } from '../api/backup';
import AiGenerateDrawer from '../components/ai-generate/AiGenerateDrawer';

const { Title } = Typography;

interface Project {
  id: string;
  name: string;
  description?: string;
  thumbnail?: string;
  updatedAt: string;
  modelCount?: number;
}

type SortKey = 'updatedDesc' | 'updatedAsc' | 'name' | 'size';

export default function ProjectList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A3：深链 /projects?ai=1&baseId=xxx（官网基座详情「用 AI 生成」直达）
  const [aiInitialBaseId, setAiInitialBaseId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (searchParams.get('ai') !== '1') return;
    setAiOpen(true);
    const baseId = searchParams.get('baseId');
    if (baseId) setAiInitialBaseId(baseId);
  }, [searchParams]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  // 2026-09-27：加载失败态（后端未启动/断网）——此前仅瞬时 toast，页面显示"暂无项目"误导用户
  const [loadError, setLoadError] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [form] = Form.useForm();
  // 2026-08-31｜搜索 / 排序 / 视图切换（卡片|列表，localStorage 持久）
  const [keyword, setKeyword] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('updatedDesc');
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => (localStorage.getItem('sceneListView') as 'card' | 'list') || 'card');
  // 导入导出权限（服务端权威；ZIP 默认仅超管可见，JSON 按套餐/额度）
  const [backupPolicy, setBackupPolicy] = useState<BackupPolicyView | null>(null);

  useEffect(() => {
    fetchBackupPolicy().then(setBackupPolicy).catch(() => setBackupPolicy(null));
  }, []);

  const exportMenuItems = useMemo(() => {
    const suffix = backupPolicy
      ? backupPolicy.json.quota < 0
        ? ' · 不限'
        : ` · 本月剩余 ${backupPolicy.json.remaining} 次`
      : '';
    const items: { key: string; label: string }[] = [
      { key: 'json', label: `JSON 元数据（模型仅引用，体积小）${suffix}` },
    ];
    if (backupPolicy?.zipAllowed) items.push({ key: 'zip', label: 'ZIP 打包（含模型文件，跨环境可用）' });
    return items;
  }, [backupPolicy]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    fetchScenes()
      .then((data) => setProjects(data as any))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // 2026-08-31｜搜索 / 排序（时间/名称/大小）
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list = projects.filter((p) => !kw || (p.name || '').toLowerCase().includes(kw) || (p.description || '').toLowerCase().includes(kw));
    const byName = (a: Project, b: Project) => (a.name || '').localeCompare(b.name || '', 'zh');
    switch (sortKey) {
      case 'updatedAsc': list = [...list].sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()); break;
      case 'name': list = [...list].sort(byName); break;
      case 'size': list = [...list].sort((a, b) => (b.modelCount ?? 0) - (a.modelCount ?? 0)); break;
      default: list = [...list].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return list;
  }, [projects, keyword, sortKey]);

  // 导出备份：JSON 元数据 / ZIP（含模型文件）两种模式按需选择（服务端强校验权限与额度）
  const handleExport = async (mode: BackupMode) => {
    try {
      setExporting(true);
      const n = await exportScenesBackup(mode);
      message.success(`已导出 ${n} 个场景（${mode === 'zip' ? 'ZIP 含模型文件' : 'JSON 元数据'}）`);
      fetchBackupPolicy().then(setBackupPolicy).catch(() => {});
    } catch (e) {
      backupExportErrorHandler(e);
    } finally {
      setExporting(false);
    }
  };

  // 导入备份：支持 .json / .zip（zip 自动提取模型文件上传到当前环境）
  const handleImportFile = async (file: File) => {
    try {
      setImporting(true);
      const ok = await importScenesBackup(file);
      message.success(`已导入 ${ok} 个场景`);
      setProjects(await fetchScenes() as any);
      return true;
    } catch (e) {
      backupErrorHandler(e);
      return false;
    } finally {
      setImporting(false);
    }
  };

  // 单个项目导出（卡片级）：两种模式（ZIP 仅超管可见）
  const handleExportOne = async (id: string, mode: BackupMode) => {
    try {
      setExporting(true);
      const n = await exportScenesBackup(mode, id);
      message.success(`已导出「${projects.find((p) => p.id === id)?.name || '场景'}」（${mode === 'zip' ? 'ZIP 含模型文件' : 'JSON 元数据'}）`);
      fetchBackupPolicy().then(setBackupPolicy).catch(() => {});
    } catch (e) {
      backupExportErrorHandler(e);
    } finally {
      setExporting(false);
    }
  };

  const handleCreate = async (values: { name: string; description: string }) => {
    try {
      const scene = await createScene({ name: values.name, description: values.description });
      setProjects((prev) => [scene as any, ...prev]);
      setModalOpen(false);
      form.resetFields();
      message.success('项目创建成功');
      window.open(`/editor/scene/${scene.id}`, '_blank');
    } catch {
      message.error('创建失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteScene(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      message.success('已移入回收站，30天后自动删除');
    } catch {
      message.error('删除失败');
    }
  };

  if (loading) {
    return <AppLoading />;
  }

  // 2026-09-27：加载失败（后端未启动/断网）——页面级错误态 + 重试，替代"暂无项目"空态误导
  if (loadError) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="warning"
          title="无法连接到服务器"
          subTitle="请检查网络连接或稍后重试；若持续失败，请联系管理员"
          extra={
            <Button type="primary" icon={<ReloadOutlined />} onClick={load}>
              重试
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>我的项目</Title>
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="按名称/描述搜索"
            style={{ width: 220 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Select
            value={sortKey}
            onChange={(v) => setSortKey(v)}
            style={{ width: 130 }}
            options={[
              { value: 'updatedDesc', label: '时间↓ 新→旧' },
              { value: 'updatedAsc', label: '时间↑ 旧→新' },
              { value: 'name', label: '名称排序' },
              { value: 'size', label: '大小（模型数）' },
            ]}
          />
          <Segmented
            value={viewMode}
            onChange={(v) => { const m = v as 'card' | 'list'; setViewMode(m); localStorage.setItem('sceneListView', m); }}
            options={[{ value: 'card', icon: <AppstoreOutlined />, label: '卡片' }, { value: 'list', icon: <BarsOutlined />, label: '列表' }]}
          />
          <Dropdown
            menu={{
              items: exportMenuItems,
              onClick: ({ key }) => handleExport(key as BackupMode),
            }}
            disabled={exporting}
          >
            <Button icon={<DownloadOutlined />} loading={exporting}>
              导出备份 ▾
            </Button>
          </Dropdown>
          <Upload
            accept=".json,.zip,application/json,application/zip"
            showUploadList={false}
            beforeUpload={(file) => { handleImportFile(file); return false; }}
            disabled={importing}
          >
            <Button icon={<UploadOutlined />} loading={importing}>
              导入备份
            </Button>
          </Upload>
          <Button icon={<DeleteRowOutlined />} onClick={() => navigate('/trash')}>
            回收站
          </Button>
          <Button icon={<ThunderboltOutlined />} onClick={() => setAiOpen(true)}>
            AI 生成
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            新建项目
          </Button>
        </Space>
      </div>

        <AiGenerateDrawer open={aiOpen} onClose={() => { setAiOpen(false); load(); }} initialBaseId={aiInitialBaseId} />

      {filtered.length === 0 ? (
        <Empty
          description={keyword ? '没有匹配的项目' : '暂无项目，点击右上角新建'}
          style={{ marginTop: 80 }}
        />
      ) : viewMode === 'list' ? (
        <Table
          rowKey="id"
          size="middle"
          dataSource={filtered}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          columns={[
            { title: '名称', dataIndex: 'name', render: (v, r) => <a onClick={() => window.open(`/editor/scene/${r.id}`, '_blank')}>{v}</a> },
            { title: '描述', dataIndex: 'description', ellipsis: true, render: (v) => v || '—' },
            { title: '模型数', dataIndex: 'modelCount', width: 90, sorter: (a: any, b: any) => (a.modelCount ?? 0) - (b.modelCount ?? 0) },
            { title: '更新时间', dataIndex: 'updatedAt', width: 120, render: (v) => new Date(v).toLocaleString('zh-CN', { dateStyle: 'short', timeStyle: 'short' }) },
            {
              title: '操作', width: 200,
              render: (_, r) => (
                <Space size={4}>
                  <Button type="link" size="small" icon={<RadarChartOutlined />} onClick={() => window.open(`/editor/scene/${r.id}`, '_blank')}>编辑</Button>
                  <Button type="link" size="small" icon={<CloudUploadOutlined />} onClick={() => useDeployStore.getState().openModal({ kind: 'scene', sceneId: r.id, name: r.name })}>发布</Button>
                  <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => handleExportOne(r.id, 'zip')}>导出</Button>
                  <Popconfirm title="确定删除该项目？" description="删除后将移入回收站，30天后自动删除" onConfirm={() => handleDelete(r.id)} okText="删除" cancelText="取消" okButtonProps={{ danger: true }}>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {filtered.map((p) => (
            <Col key={p.id} xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                cover={
                  <div style={{ height: 200, background: '#0f0f2e', overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                    onClick={() => window.open(`/editor/scene/${p.id}`, '_blank')}>
                    {p.thumbnail ? (
                      <img src={p.thumbnail} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <RadarChartOutlined style={{ fontSize: 44, color: '#1890ff', opacity: 0.4 }} />
                      </div>
                    )}
                    {p.modelCount ? <span style={{ position: 'absolute', top: 6, right: 8, fontSize: 11, color: '#aaa', background: 'rgba(0,0,0,0.5)', padding: '1px 6px', borderRadius: 3 }}>{p.modelCount} 模型</span> : null}
                    {/* 半透明渐变遮罩 + 名称/描述/时间叠加在预览图底部（预览图窗口扩大到 actions 上方整块） */}
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '26px 12px 10px', background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0.82) 100%)' }}>
                      <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '20px' }}>
                        {p.name}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '18px', marginTop: 2 }}>
                        {p.description || '暂无描述'}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 2 }}>
                        更新于 {new Date(p.updatedAt).toLocaleDateString('zh-CN')}
                      </div>
                    </div>
                  </div>
                }
                actions={[
                  <Button type="link" icon={<RadarChartOutlined />} onClick={() => window.open(`/editor/scene/${p.id}`, '_blank')}>
                    编辑
                  </Button>,
                  <Button type="link" icon={<CloudUploadOutlined />} onClick={() => useDeployStore.getState().openModal({ kind: 'scene', sceneId: p.id, name: p.name })}>
                    发布
                  </Button>,
                  <Dropdown
                    key="export"
                    trigger={['click']}
                    disabled={exporting}
                    menu={{
                      items: [
                        { key: 'json', label: '导出 JSON（引用）' },
                        ...(backupPolicy?.zipAllowed ? [{ key: 'zip', label: '导出 ZIP（含模型）' }] : []),
                      ],
                      onClick: ({ key }) => handleExportOne(p.id, key as BackupMode),
                    }}
                  >
                    <Button type="link" icon={<DownloadOutlined />} onClick={(e) => e.preventDefault()}>
                      导出
                    </Button>
                  </Dropdown>,
                  <Popconfirm
                    title="确定删除该项目？"
                    description="删除后将移入回收站，30天后自动删除"
                    onConfirm={() => handleDelete(p.id)}
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />}>
                      删除
                    </Button>
                  </Popconfirm>,
                ]}
              >
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title="新建项目"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="项目名称" rules={[{ required: true, message: '请输入项目名称' }]}>
            <Input placeholder="例如：智慧园区数字孪生" />
          </Form.Item>
          <Form.Item name="description" label="项目描述">
            <Input.TextArea rows={3} placeholder="简要描述项目内容" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
